<template>
  <Teleport to="body">
    <button v-show="!open && !hidden && showLauncher" ref="launcher" class="zeus-fab" :class="{ 'is-dragging': launcherDrag.dragging.value }" :style="launcherDrag.style.value" type="button" aria-label="打开 AI 分析助手" aria-controls="zeus-assistant-panel" :aria-expanded="open" title="点击打开，拖动可移动" @click="show"
      @pointerdown="launcherDrag.start" @pointermove="launcherDrag.update" @pointerup="launcherDrag.end" @pointercancel="launcherDrag.end" @lostpointercapture="launcherDrag.end" @keydown="launcherDrag.onKeydown">
      <span class="zeus-avatar" aria-hidden="true">✦</span>问问 AI
    </button>
    <aside v-show="open && !hidden" ref="panel" id="zeus-assistant-panel" class="zeus-panel" :class="{ 'is-dragging': panelDrag.dragging.value }" :style="panelDrag.style.value" aria-label="AI 分析助手" @keydown.esc="minimize">
      <header @pointerdown="!$event.target.closest('button') && panelDrag.start($event)" @pointermove="panelDrag.update" @pointerup="panelDrag.end" @pointercancel="panelDrag.end" @lostpointercapture="panelDrag.end">
        <div class="zeus-panel-handle" tabindex="0" aria-label="移动 AI 分析助手窗口" title="拖动移动，也可使用方向键" @keydown="panelDrag.onKeydown">
          <span class="zeus-avatar" aria-hidden="true">✦</span><strong>AI 分析助手</strong>
        </div>
        <div class="zeus-panel-actions">
          <button type="button" aria-label="最小化 AI 分析助手" title="最小化，保留当前会话" @click="minimize">−</button>
          <button type="button" aria-label="关闭 AI 分析助手" title="关闭，下次打开新对话" @click="close">×</button>
        </div>
      </header>
      <div v-if="error" class="zeus-state" role="alert">{{ error }}<button type="button" @click="connect">重试连接</button></div>
      <div v-else-if="!ready || resetting" class="zeus-state" role="status">{{ resetting ? '正在打开新对话…' : '正在准备 AI 分析助手…' }}</div>
      <iframe v-if="url" v-show="!error && !resetting" ref="frame" :src="url" title="AI 分析助手对话" @load="handshake" />
    </aside>
    <div v-if="launcherDrag.dragging.value || panelDrag.dragging.value" class="zeus-drag-shield" aria-hidden="true" />
  </Teleport>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { connectOpenCode, buildOpenCodeAppUrl } from '@/utils/opencode'
import { createNavigationHandler } from '@/utils/platformNavigation'
import { getPublicPromptTemplates } from '@/api/promptTemplate'
import { listAnalysisTemplates } from '@/api/inspiration'
import { assistantPageIdentity } from '@/composables/useAssistantPage'
import { useFloatingDrag } from '@/composables/useFloatingDrag'
import { ZEUS_ASK_EVENT, ZEUS_ASK_MESSAGE } from '@/utils/zeusAsk'
import { collectElements } from '@/zeus/collect'
import { createUiBridge } from '@/zeus/bridge'

// hidden：项目工作区里只隐藏不卸载（浮标与面板经 Teleport 挂在 body 下，外层容器的 v-show 管不到）。
// showLauncher：只控制浮标；不显示浮标的页面仍可由数聚 AI 模式等经事件打开面板。
const props = defineProps({ identityId: [Number, String], context: Object, hidden: Boolean, showLauncher: { type: Boolean, default: true } })
const router = useRouter()
const route = useRoute()
const open = ref(false)
const launcher = ref(null)
const panel = ref(null)
const launcherDrag = useFloatingDrag(launcher)
const panelDrag = useFloatingDrag(panel)
const resetting = ref(false)
let resetOnOpen = false
let resetRevision = 0
const frame = ref(null)
const url = ref('')
const ready = ref(false)
const error = ref('')
let alive = true
let connecting = false
let timer
let tokenTimer
let lastFocus
let sentToken = ''
let navigation
let attempts = 0
let questionEpoch = 0
let questions = []
let recommendations = 'loading'
let conversationRevision = 0
// 数聚 AI 模式交来的问题：助手就绪且页面上下文已发出后再转交（只保留最新一条）。
let pendingAsk = null
let askSequence = 0
const currentPageIdentity = () => JSON.stringify([route.path, assistantPageIdentity(props.context)])
let pageIdentity = currentPageIdentity()
const targetOrigin = () => url.value ? new URL(url.value, location.href).origin : ''
const currentIdentity = () => {
  try { return String(JSON.parse(localStorage.getItem('user') || '{}').identityId || '') === String(props.identityId) }
  catch { return false }
}
const send = data => { if (currentIdentity() && targetOrigin()) frame.value?.contentWindow?.postMessage(data, targetOrigin()) }
const pageKey = () => props.context?.pageKey || ''
const elements = () => { try { return collectElements(pageKey()) } catch { return { pageKey: pageKey(), items: [] } } }
const uiBridge = createUiBridge({ source: () => frame.value?.contentWindow, origin: targetOrigin, pageKey, post: send })
const publish = () => { if (props.context) send({ type: 'gutdb_assistant_context', context: props.context, elements: elements(), conversationRevision, questions, recommendations, ...(resetting.value ? { resetRevision } : {}) }) }
const handshake = () => {
  clearTimeout(timer)
  if (++attempts > 45) { error.value = '助手加载超时，请重试连接。'; return }
  send({ type: 'gutdb_assistant_ping' })
  if (!ready.value && alive) timer = setTimeout(handshake, 1000)
}
async function connect() {
  if (connecting || !props.identityId || !currentIdentity()) return
  connecting = true
  error.value = ''
  attempts = 0
  try {
    const response = await connectOpenCode(localStorage.getItem('token'))
    if (!alive || !currentIdentity()) return
    if (!response.data?.data?.port || response.data.data.status === 'error') throw new Error('实例尚未就绪')
    sentToken = localStorage.getItem('token') || ''
    if (url.value && ready.value) send({ type: 'gutdb_token_refresh', token: sentToken })
    else {
      const base = buildOpenCodeAppUrl({ origin: location.origin, hostname: location.hostname, token: sentToken })
      const next = new URL(base)
      next.pathname = next.pathname.replace(/\/$/, '') + '/assistant'
      url.value = ''
      await nextTick()
      url.value = next.toString()
      ready.value = false
      navigation = createNavigationHandler({ source: () => frame.value?.contentWindow, origin: next.origin,
        listTemplates: listAnalysisTemplates,
        navigate: target => router.push(target), openProjects: async () => { await nextTick(); window.dispatchEvent(new CustomEvent('gutdb-xbios-action', { detail: { action: 'open_project' } })) } })
      await nextTick()
      handshake()
    }
  } catch { if (alive) error.value = '助手暂时无法连接。草稿和已有记录会保留，请稍后重试。' }
  finally { connecting = false }
}
async function show(event) {
  if (launcherDrag.wasDrag(event)) return
  if (resetOnOpen) {
    resetOnOpen = false
    resetting.value = true
    resetRevision++
  }
  lastFocus = document.activeElement
  open.value = true
  void panelDrag.refresh()
  attempts = 0
  void loadQuestions()
  if (!url.value || error.value) await connect()
  else { handshake(); publish() }
}
function flushAsk() {
  if (!pendingAsk || !ready.value || resetting.value) return
  send({ type: ZEUS_ASK_MESSAGE, ...pendingAsk })
  pendingAsk = null
}
function onAsk(event) {
  const text = String(event.detail?.text || '').trim().slice(0, 4000)
  if (!text) return
  pendingAsk = { text, askId: ++askSequence }
  void show().then(flushAsk)
}
function minimize() {
  open.value = false
  // 拾取中收起：撤掉拾取层并告诉 iframe，否则它一直卡在"拾取中"。
  uiBridge.cancelPick({ notify: true })
  void launcherDrag.refresh().then(() => (lastFocus?.isConnected ? lastFocus : launcher.value)?.focus?.())
}
// 换到不显示浮标的页面时收起面板（对话保留），回到标准流程页可从浮标继续。
watch([() => route.path, () => props.showLauncher], () => {
  if (!props.showLauncher && open.value) minimize()
}, { flush: 'post' })
function close() {
  resetOnOpen = true
  minimize()
}
function receive(event) {
  if (event.source !== frame.value?.contentWindow || event.origin !== targetOrigin() || !currentIdentity()) return
  // 报告 iframe 是 srcdoc，内部 DOM 变化观察不到：拾取前先刷新清单。
  if (event.data?.type === 'gutdb_ui_pick_start') publish()
  if (uiBridge.handle(event)) return
  if (event.data?.type === 'xbios_assistant_ready') {
    error.value = ''
    ready.value = true
    clearTimeout(timer)
    publish()
    flushAsk()
  }
  if (event.data?.type === 'xbios_assistant_reset_ready' && event.data.resetRevision === resetRevision) {
    resetting.value = false
    flushAsk()
  }
  if (event.data?.type === 'xbios_assistant_minimize') minimize()
  if (event.data?.type === 'gutdb_token_expired') void connect()
  void navigation?.(event)
}
async function loadQuestions() {
  const epoch = ++questionEpoch
  questions = []
  recommendations = 'loading'
  if (ready.value) publish()
  if (!url.value && !open.value) return
  try {
    const response = await getPublicPromptTemplates(['assistant.page_questions'], 0, props.context || {})
    if (response.data?.code !== 200) throw new Error('模板接口不可用')
    if (!alive || epoch !== questionEpoch || !currentIdentity()) return
    questions = response.data?.data?.slots?.['assistant.page_questions'] || []
    recommendations = 'ready'
  } catch {
    if (!alive || epoch !== questionEpoch) return
    recommendations = 'error'
  }
  if (ready.value) publish()
}
watch([() => route.path, () => props.context], () => {
  const next = currentPageIdentity()
  if (next !== pageIdentity) {
    pageIdentity = next
    // 换页后旧页的拾取目标已失效。
    uiBridge.cancelPick({ notify: true })
    conversationRevision++
  }
  void loadQuestions()
}, { deep: true })
let observer
let observeTimer
const OWN = '.zeus-ui-highlight, .zeus-ui-tip'
// 自己画的高亮框/气泡、宙斯面板自身的变化不触发重采。
const ownMutation = record => {
  if (record.target.closest?.(`${OWN}, #zeus-assistant-panel`)) return true
  const nodes = [...record.addedNodes, ...record.removedNodes]
  return record.type === 'childList' && nodes.length > 0 && nodes.every(node => node.matches?.(OWN))
}
const schedulePublish = () => {
  clearTimeout(observeTimer)
  observeTimer = setTimeout(() => { if (open.value && ready.value) publish() }, 300)
}
onMounted(() => {
  observer = new MutationObserver(records => { if (!records.every(ownMutation)) schedulePublish() })
  observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'aria-selected', 'aria-expanded', 'aria-disabled', 'class', 'style', 'hidden', 'title'] })
  window.addEventListener('message', receive)
  window.addEventListener(ZEUS_ASK_EVENT, onAsk)
  tokenTimer = setInterval(() => {
    const token = localStorage.getItem('token') || ''
    if (ready.value && token && token !== sentToken && currentIdentity()) {
      sentToken = token
      send({ type: 'gutdb_token_refresh', token })
    }
  }, 5000)
})
onBeforeUnmount(() => {
  alive = false
  observer?.disconnect()
  clearTimeout(observeTimer)
  uiBridge.dispose()
  clearTimeout(timer)
  clearInterval(tokenTimer)
  window.removeEventListener('message', receive)
  window.removeEventListener(ZEUS_ASK_EVENT, onAsk)
})
</script>

<style scoped>
.zeus-fab { position: fixed; right: 24px; bottom: 112px; z-index: 1900; display: flex; align-items: center; gap: 9px; padding: 9px 16px 9px 9px; border: 1px solid #d8e3ff; border-radius: 30px; background: #fff; color: #0840e6; box-shadow: 0 6px 26px #1f459e24; font: inherit; font-size: 13px; cursor: pointer; }
.zeus-avatar { display: grid; place-items: center; width: 32px; height: 32px; border-radius: 11px; background: linear-gradient(135deg,#174feb,#8055d9); color: white; font-size: 22px; }
.zeus-panel { position: fixed; bottom: 12px; right: 12px; width: 420px; height: min(720px, calc(100dvh - 24px)); max-width: calc(100vw - 24px); z-index: 1901; display: flex; flex-direction: column; background: #fff; border: 1px solid #dfe5f0; border-radius: 16px; box-shadow: -10px 0 45px #21375622; overflow: hidden; }
.zeus-panel header { display: flex; gap: 10px; align-items: center; padding: 16px; flex-shrink: 0; touch-action: none; cursor: move; user-select: none; border-bottom: 1px solid #edf0f6; color: #26324a; }
.zeus-panel strong { font-size: 15px; }
.zeus-panel-handle { display: flex; flex: 1; align-items: center; gap: 10px; min-width: 0; border-radius: 6px; }
.zeus-panel-actions { display: flex; gap: 8px; }
.zeus-fab { touch-action: none; user-select: none; }
.is-dragging, .is-dragging header { cursor: grabbing; }
.zeus-drag-shield { position: fixed; inset: 0; z-index: 1902; cursor: grabbing; touch-action: none; }
.zeus-panel-handle:focus-visible, .zeus-panel header button:focus-visible, .zeus-fab:focus-visible { outline: 2px solid #5280ed; outline-offset: 2px; }
.zeus-panel header button { border: 0; background: #f3f5f9; border-radius: 7px; width: 30px; height: 30px; cursor: pointer; font-size: 22px; }
.zeus-panel iframe { flex: 1; min-height: 0; width: 100%; border: 0; }
.zeus-state { padding: 16px; color: #67748b; font-size: 13px; line-height: 1.7; } .zeus-state button { display: block; margin-top: 8px; color: #0840e6; background: #f3f6ff; border: 0; border-radius: 6px; padding: 8px 12px; cursor: pointer; }
@media(max-width: 760px) { .zeus-panel { bottom: 8px; right: 8px; width: min(420px, calc(100vw - 16px)); height: min(720px, calc(100dvh - 16px)); max-width: none; } }
</style>
